import type { DeepDive } from "./deepDive";

/**
 * Pre-authored deep dives for a sample of cards that currently lead the live feed, grounded
 * strictly in each card's own reported content. Keyed by card id. A fresh reader meets these near
 * the top of the feed. No em dashes or en dashes anywhere.
 *
 * This is demo seed content for gathering feedback on the experience. The production path is a
 * grounded, gate-checked pipeline pass that writes a `deep_dive` field for every new card.
 */
export const DEEP_DIVES: Record<string, DeepDive> = {
  // Qld triple-zero after Telstra outage (Technology & Tools)
  "draft-45a3e4c056819a30": {
    seconds: 40,
    takeaway:
      "A nationwide Telstra outage stopped 21 triple-zero calls from connecting in Queensland, exposing how fragile emergency access is when one network fails.",
    beats: [
      { phase: "background", heading: "Triple-zero rides on the network", line: "Emergency calls in Queensland depend on Telstra's telecom network working." },
      { phase: "trigger", heading: "A nationwide outage", line: "On Wednesday, a nationwide Telstra disruption knocked out service across the state." },
      { phase: "event", heading: "21 calls failed", line: "Queensland Police confirmed 21 triple-zero calls failed to connect during the outage." },
      { phase: "impact", heading: "Reaching help got harder", line: "Authorities urged residents to use alternative emergency numbers wherever possible." },
      { phase: "why", heading: "A resilience question", line: "The failure exposes gaps in emergency response and raises questions about how resilient Australia's telecom networks are." },
    ],
    keyFacts: [
      { label: "Failed 000 calls", value: "21", icon: "number", hint: "Confirmed by Queensland Police" },
      { label: "Where", value: "Queensland", icon: "place" },
      { label: "Trigger", value: "Telstra outage", icon: "spark", hint: "Nationwide disruption" },
      { label: "When", value: "Wednesday", icon: "time" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Telstra network fails", kind: "cause", caption: "Nationwide outage" },
        { id: "n2", label: "Triple-zero calls drop", kind: "pivot", caption: "The emergency line rides on that network" },
        { id: "n3", label: "Police check 21 callers", kind: "effect", caption: "Tracing who could not get through" },
        { id: "n4", label: "Calls for network resilience", kind: "outcome", caption: "Scrutiny of emergency backups" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "knocks out" },
        { from: "n2", to: "n3", label: "so" },
        { from: "n2", to: "n4", label: "raises" },
      ],
    },
    questions: [
      { q: "What happens to triple-zero when a network fails?" },
      { q: "How were the 21 affected callers followed up?" },
      { q: "Could a backup network have kept calls connected?" },
      { q: "Has an outage hit emergency calls before?" },
    ],
  },

  // Lovable valuation (Technology & Tools)
  "draft-f5f511f5967481fe": {
    seconds: 30,
    takeaway:
      "A reported $300M round could roughly double Lovable's valuation to about $13.2B, a sign of how hard capital is still chasing high-growth software.",
    beats: [
      { phase: "background", heading: "A fast-rising startup", line: "Software startup Lovable has been drawing strong investor interest." },
      { phase: "trigger", heading: "A new round in talks", line: "Lovable is reportedly negotiating a fresh funding round." },
      { phase: "event", heading: "The valuation could double", line: "The round, expected to total $300 million, could roughly double Lovable's valuation to about $13.2 billion, with Menlo Ventures reportedly leading." },
      { phase: "why", heading: "A signal for the sector", line: "A valuation this size shows how aggressively capital is chasing high-growth software despite broader economic swings." },
    ],
    keyFacts: [
      { label: "New valuation", value: "~$13.2B", icon: "money", hint: "Roughly double the last mark" },
      { label: "Round size", value: "$300M", icon: "money" },
      { label: "Lead investor", value: "Menlo Ventures", icon: "people", hint: "Reportedly leading" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Strong investor demand", kind: "cause", caption: "Lovable draws interest" },
        { id: "n2", label: "$300M round in talks", kind: "pivot" },
        { id: "n3", label: "Valuation near $13.2B", kind: "effect", caption: "Roughly double" },
        { id: "n4", label: "Signals a hot software market", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "fuels" },
        { from: "n2", to: "n3", label: "lifts" },
        { from: "n3", to: "n4", label: "signals" },
      ],
    },
    questions: [
      { q: "Why is the valuation doubling?" },
      { q: "Who is Menlo Ventures?" },
      { q: "What does Lovable actually build?" },
      { q: "Is a round this size normal right now?" },
    ],
  },

  // Brock Purdy vs Baker Mayfield (Sport & Play)
  "draft-563deac352ca5aed": {
    seconds: 30,
    takeaway:
      "With two similar quarterbacks, one analyst gives Purdy the 2026 edge on efficiency and leadership, a call that shapes the 49ers' playoff outlook.",
    beats: [
      { phase: "background", heading: "Two similar quarterbacks", line: "Brock Purdy and Baker Mayfield are alike in size and production." },
      { phase: "event", heading: "One analyst picks Purdy", line: "An analyst believes Purdy holds the edge for the 2026 season." },
      { phase: "impact", heading: "Why Purdy gets the nod", line: "The 49ers quarterback's efficiency and leadership are cited as his key advantages." },
      { phase: "why", heading: "Stakes beyond the debate", line: "Quarterback performance directly shapes team success and playoff contention in the NFL." },
    ],
    keyFacts: [
      { label: "Player favoured", value: "Brock Purdy", icon: "people" },
      { label: "Compared with", value: "Baker Mayfield", icon: "people" },
      { label: "Season", value: "2026", icon: "time" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Purdy and Mayfield, alike", kind: "cause", caption: "Similar size and output" },
        { id: "n2", label: "Analyst rates Purdy higher", kind: "pivot" },
        { id: "n3", label: "Efficiency + leadership cited", kind: "effect" },
        { id: "n4", label: "Outlook favours the 49ers", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "prompts" },
        { from: "n2", to: "n3", label: "because" },
        { from: "n3", to: "n4", label: "so" },
      ],
    },
    questions: [
      { q: "Why does the analyst favour Purdy?" },
      { q: "How do the two compare statistically?" },
      { q: "What is Mayfield's situation with the Buccaneers?" },
      { q: "What does this mean for the 49ers' season?" },
    ],
  },

  // Conor McGregor UFC 329 (Sport & Play)
  "draft-a8681ae777eace78": {
    seconds: 35,
    takeaway:
      "McGregor waves off doubts about his hunger before UFC 329, a comeback that tests whether his star power still translates inside the cage.",
    beats: [
      { phase: "background", heading: "A comeback under doubt", line: "Speculation has swirled about Conor McGregor's focus ahead of his return." },
      { phase: "trigger", heading: "He hits back", line: "The former champion calls it silly to question his hunger before UFC 329 this Saturday." },
      { phase: "event", heading: "A loaded card", line: "CBS Sports notes the event carries big storylines around his comeback, alongside Max Holloway." },
      { phase: "why", heading: "A test of star power", line: "His performance will show whether he can still compete at the highest level, a pivotal moment for the UFC." },
    ],
    keyFacts: [
      { label: "Event", value: "UFC 329", icon: "flag" },
      { label: "When", value: "Saturday", icon: "time" },
      { label: "Also on the card", value: "Max Holloway", icon: "people" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Doubts about his hunger", kind: "cause" },
        { id: "n2", label: "He dismisses the concerns", kind: "pivot" },
        { id: "n3", label: "Returns at UFC 329", kind: "effect" },
        { id: "n4", label: "Result tests his comeback", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "he answers" },
        { from: "n2", to: "n3", label: "then" },
        { from: "n3", to: "n4", label: "and" },
      ],
    },
    questions: [
      { q: "What sparked the doubts about McGregor?" },
      { q: "Who else is on the UFC 329 card?" },
      { q: "Why is this comeback such a big test?" },
      { q: "When and where is the fight?" },
    ],
  },

  // Union safety warning before derailment (Money & Work)
  "draft-9e5d56c782eaa1a0": {
    seconds: 40,
    takeaway:
      "A rail union's safety warning was followed a day later by a major derailment, putting corporate accountability squarely under investigation.",
    beats: [
      { phase: "background", heading: "A warning is raised", line: "A rail union says it issued formal safety warnings to a freight operator about specific risks." },
      { phase: "trigger", heading: "One day later, a derailment", line: "About 24 hours after, a major derailment hit the New South Wales Southern Highlands." },
      { phase: "event", heading: "Freight thrown into chaos", line: "The derailment caused significant disruption to the regional freight network." },
      { phase: "impact", heading: "Now under investigation", line: "Authorities are examining whether the union's prior warnings were adequately addressed by management." },
      { phase: "why", heading: "Accountability in focus", line: "The timing raises serious questions about corporate accountability and preventing avoidable industrial accidents." },
    ],
    keyFacts: [
      { label: "Warning lead time", value: "~24 hours", icon: "time", hint: "Before the tracks were compromised" },
      { label: "Where", value: "NSW Southern Highlands", icon: "place" },
      { label: "Network hit", value: "Regional freight", icon: "scale" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Union warns of safety risks", kind: "cause" },
        { id: "n2", label: "Warning goes unresolved", kind: "pivot", caption: "About 24 hours pass" },
        { id: "n3", label: "Major derailment", kind: "effect" },
        { id: "n4", label: "Investigation into the warnings", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "then" },
        { from: "n2", to: "n3", label: "a derailment" },
        { from: "n3", to: "n4", label: "triggers" },
      ],
    },
    questions: [
      { q: "What risks did the union warn about?" },
      { q: "How soon after the warning was the derailment?" },
      { q: "Who is being investigated?" },
      { q: "What happens to regional freight now?" },
    ],
  },

  // Brazil climate disasters (Planet & Environment)
  "draft-b66c76345e645629": {
    seconds: 40,
    takeaway:
      "Across 33 years, 90% of Brazil's cities were hit by climate disasters, with floods and landslides alone causing $30B in damage, a warning about adaptation everywhere.",
    beats: [
      { phase: "background", heading: "A 33-year record", line: "Researchers analysed 60,000 hydrogeological disaster records from 1991 to 2024." },
      { phase: "event", heading: "Nine in ten cities hit", line: "They found 90% of Brazil's 5,570 cities experienced climate-related disasters." },
      { phase: "impact", heading: "$30 billion in damage", line: "Floods and landslides alone caused $30 billion in damage since 2000, with events linked to El Nino cycles." },
      { phase: "why", heading: "A call for policy", line: "The findings urge targeted public policies and show the global need for climate adaptation." },
    ],
    keyFacts: [
      { label: "Cities affected", value: "90% of 5,570", icon: "scale" },
      { label: "Records analysed", value: "60,000", icon: "number", hint: "Hydrogeological disasters" },
      { label: "Damage since 2000", value: "$30 billion", icon: "money", hint: "Floods and landslides alone" },
      { label: "Period", value: "1991-2024", icon: "time" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "El Nino cycles", kind: "cause" },
        { id: "n2", label: "Floods and landslides", kind: "pivot" },
        { id: "n3", label: "90% of cities hit", kind: "effect" },
        { id: "n4", label: "$30B in damage", kind: "effect" },
        { id: "n5", label: "Calls for adaptation policy", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "drive" },
        { from: "n2", to: "n3" },
        { from: "n2", to: "n4" },
        { from: "n3", to: "n5" },
        { from: "n4", to: "n5" },
      ],
    },
    questions: [
      { q: "How was this measured across so many cities?" },
      { q: "What is driving the disasters?" },
      { q: "Which regions are worst hit?" },
      { q: "What can climate adaptation actually do?" },
    ],
  },

  // Europe pollinator crisis (Science & Systems)
  "draft-fdddfb6920cff301": {
    seconds: 35,
    takeaway:
      "135 researchers warn Europe's wild pollinators are declining unsustainably, a threat that reaches all the way to the continent's food security.",
    beats: [
      { phase: "background", heading: "Pollinators underpin the continent", line: "Wild pollinators are essential for ecosystem health and agriculture across Europe." },
      { phase: "trigger", heading: "135 researchers sound the alarm", line: "A new white paper from 135 researchers warns of a severe ecological crisis if their decline continues." },
      { phase: "event", heading: "Evidence from eight consortia", line: "The report synthesises findings from eight major research projects, including BUTTERFLY and PolinERA." },
      { phase: "impact", heading: "Trends called unsustainable", line: "Experts from Aarhus University say the current declines are unsustainable." },
      { phase: "why", heading: "Food security at stake", line: "A pollinator collapse threatens European food security and ecosystem stability, needing immediate, coordinated policy action." },
    ],
    keyFacts: [
      { label: "Researchers", value: "135", icon: "people" },
      { label: "Research consortia", value: "8", icon: "number" },
      { label: "Includes", value: "BUTTERFLY, PolinERA", icon: "flag" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Wild pollinators decline", kind: "cause" },
        { id: "n2", label: "135 researchers warn", kind: "pivot" },
        { id: "n3", label: "Threat to crops + biodiversity", kind: "effect" },
        { id: "n4", label: "Call for coordinated policy", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "prompts" },
        { from: "n2", to: "n3", label: "flags" },
        { from: "n3", to: "n4", label: "so" },
      ],
    },
    questions: [
      { q: "Who is behind the warning?" },
      { q: "Why do pollinators matter so much?" },
      { q: "What is driving the decline?" },
      { q: "What policy action is proposed?" },
    ],
  },

  // Amazon Indigenous knowledge loss (Science & Systems)
  "draft-6e794f5c53b4df4d": {
    seconds: 40,
    takeaway:
      "Climate change and language loss are erasing Amazonian plant knowledge, 60% of which is bound to endangered languages, faster than it can be recorded.",
    beats: [
      { phase: "background", heading: "A living library", line: "More than 400 Amazonian Indigenous groups pass down knowledge of over 10,000 plant species through oral tradition." },
      { phase: "trigger", heading: "Two forces converge", line: "A University of Zurich study finds climate change and language loss are dismantling this biocultural heritage." },
      { phase: "event", heading: "Knowledge tied to language", line: "60% of plant-use knowledge is bound to endangered languages, so it fades as those languages disappear." },
      { phase: "impact", heading: "The loss speeds up", line: "Rising temperatures and shrinking habitats accelerate the erosion." },
      { phase: "why", heading: "Why it matters", line: "Preserving this knowledge is critical for biodiversity conservation and climate resilience, prompting calls to document it before it vanishes." },
    ],
    keyFacts: [
      { label: "Indigenous groups", value: "400+", icon: "people" },
      { label: "Plant species", value: "10,000+", icon: "number" },
      { label: "Knowledge at risk", value: "60%", icon: "scale", hint: "Tied to endangered languages" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Climate change + habitat loss", kind: "cause" },
        { id: "n2", label: "Endangered languages", kind: "cause" },
        { id: "n3", label: "60% of plant knowledge tied to them", kind: "pivot" },
        { id: "n4", label: "Biocultural heritage erodes", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n4" },
        { from: "n2", to: "n3", label: "carry" },
        { from: "n3", to: "n4", label: "so" },
      ],
    },
    questions: [
      { q: "How is this knowledge stored?" },
      { q: "Why is language loss so damaging?" },
      { q: "What could preserve it in time?" },
      { q: "Who carried out the study?" },
    ],
  },

  // V/Line halted after Telstra outage (Power & Policy)
  "draft-d16bdf9b753160b1": {
    seconds: 40,
    takeaway:
      "A Telstra outage halted Victoria's V/Line trains indefinitely, showing how a single telecom failure can freeze critical public infrastructure.",
    beats: [
      { phase: "background", heading: "Rail leans on telecom", line: "Victoria's V/Line network depends on telecommunications to run." },
      { phase: "trigger", heading: "A nationwide Telstra outage", line: "A massive Telstra outage knocked out services for more than 24 hours." },
      { phase: "event", heading: "Trains stop indefinitely", line: "V/Line regional rail was suspended indefinitely, at a complete standstill across the state." },
      { phase: "impact", heading: "Commuters pay the price", line: "Passengers faced lost time and higher fuel costs finding alternative travel while crews worked to restore the network." },
      { phase: "why", heading: "Fragile by design", line: "The indefinite suspension shows how badly telecommunications failures can hit critical public infrastructure and daily mobility." },
    ],
    keyFacts: [
      { label: "Network", value: "V/Line (Victoria)", icon: "flag" },
      { label: "Downtime", value: "24+ hours", icon: "time" },
      { label: "Status", value: "Suspended indefinitely", icon: "scale" },
      { label: "Trigger", value: "Telstra outage", icon: "spark" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Nationwide Telstra outage", kind: "cause" },
        { id: "n2", label: "V/Line network fails", kind: "pivot", caption: "Rail leans on telecom" },
        { id: "n3", label: "Regional trains halted 24h+", kind: "effect" },
        { id: "n4", label: "Commuters stranded, costs rise", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n2", label: "knocks out" },
        { from: "n2", to: "n3", label: "so" },
        { from: "n3", to: "n4", label: "and" },
      ],
    },
    questions: [
      { q: "Why did a telecom outage stop the trains?" },
      { q: "How long were services down?" },
      { q: "What did it cost commuters?" },
      { q: "Is the system being reviewed?" },
    ],
  },

  // AFL Fremantle vs Sydney (Sport & Play)
  "draft-aeac51122090bc1f": {
    seconds: 35,
    takeaway:
      "Two in-form top-four sides meet at Perth Stadium in a clash that could reshape the 2026 premiership race.",
    beats: [
      { phase: "background", heading: "Two top-four sides collide", line: "Fremantle and Sydney both sit in the top four heading into their clash at Perth Stadium." },
      { phase: "trigger", heading: "Form on the line", line: "Fremantle arrive off a 12-point win over Geelong, while Sydney bring a six-game winning streak." },
      { phase: "event", heading: "A ladder-shaping showdown", line: "With premiership hopes at stake, the result could reshape the top of the ladder." },
      { phase: "impact", heading: "Home-ground edge", line: "Fremantle's home advantage at Perth Stadium adds pressure to the visitors." },
      { phase: "why", heading: "Why it matters", line: "A win could propel either side into premiership contention for the 2026 season." },
    ],
    keyFacts: [
      { label: "Venue", value: "Perth Stadium", icon: "place", hint: "Fremantle's home ground" },
      { label: "Fremantle last round", value: "Won by 12", icon: "trend", hint: "Over Geelong" },
      { label: "Sydney form", value: "6-game streak", icon: "trend" },
      { label: "Ladder", value: "Both top four", icon: "scale" },
    ],
    map: {
      nodes: [
        { id: "n1", label: "Fremantle beat Geelong (+12)", kind: "cause" },
        { id: "n2", label: "Sydney on a 6-game streak", kind: "cause" },
        { id: "n3", label: "Top-four showdown at Perth", kind: "pivot" },
        { id: "n4", label: "Could reshape the title race", kind: "outcome" },
      ],
      edges: [
        { from: "n1", to: "n3" },
        { from: "n2", to: "n3" },
        { from: "n3", to: "n4", label: "so" },
      ],
    },
    questions: [
      { q: "What form are the two teams in?" },
      { q: "Why is this match so important?" },
      { q: "Who has the home advantage?" },
      { q: "What is at stake on the ladder?" },
    ],
  },
};
